const DEPLOY_ENVS = new Set(["development", "test", "staging", "production"]);
const SHARED_RESULT_URL = "https://quiz.color-analysis.pro/form/view/226461";

const PRODUCTION_URL_MARKERS = [
  "https://payment.color-analysis.pro",
  "https://quiz2.color-analysis.pro",
  "https://quiz.color-analysis.pro",
  "https://color-backend-s7hn.onrender.com",
];

const KNOWN_PRODUCTION_STRIPE_OBJECT_HASHES = new Set([
  "04df0adb",
  "425a75ee",
  "46ac4b82",
  "750e78c7",
  "9bb1180a",
  "b8adeb55",
  "ba0ad6a3",
  "cf1e4596",
  "e9e4c5c5",
  "fdfeecaf",
]);

const TEST_DEFAULTS = {
  stripePublicKey: "pk_test_color_analysis_placeholder",
  apiUrl: "http://localhost:4000",
  stripePriceId: "price_test_color_analysis_usd",
  euStripePriceId: "price_test_color_analysis_eur",
  redirectLink: "https://app.color-analysis.pro",
  paymentAppBaseUrl: "https://payment.color-analysis.pro",
  quizImageBaseUrl: "https://quiz.color-analysis.pro",
  quizPaywallEventUrl: "https://quiz2.color-analysis.pro/api/paywall/event",
  upsellOffers: {
    combo: {
      offerId: "combo",
      priceId: "price_test_color_analysis_combo",
      productId: "prod_test_color_analysis_combo",
    },
    hair: {
      offerId: "hair",
      priceId: "price_test_color_analysis_hair",
      productId: "prod_test_color_analysis_hair",
    },
    outfits: {
      offerId: "outfits",
      priceId: "price_test_color_analysis_outfits",
      productId: "prod_test_color_analysis_outfits",
    },
  },
};

const DEVELOPMENT_DEFAULTS = {
  ...TEST_DEFAULTS,
  paymentAppBaseUrl: "http://localhost:3000",
  quizImageBaseUrl: "http://localhost:3001",
  quizPaywallEventUrl: "http://localhost:3001/api/paywall/event",
};

export function createPaymentConfig(env = process.env) {
  const deployEnv = normalizeDeployEnv(env.REACT_APP_PAYMENT_DEPLOY_ENV, env.NODE_ENV);
  const allowTestDefaults = deployEnv === "test" || deployEnv === "development";
  const defaults = deployEnv === "development" ? DEVELOPMENT_DEFAULTS : TEST_DEFAULTS;

  const config = {
    deployEnv,
    stripePublicKey: getRequiredEnv(
      env,
      "REACT_APP_STRIPE_PUBLIC_KEY",
      allowTestDefaults ? defaults.stripePublicKey : undefined,
    ),
    apiUrl: getRequiredEnv(
      env,
      "REACT_APP_PAYMENT_BACKEND_API_URL",
      allowTestDefaults ? defaults.apiUrl : undefined,
    ),
    stripePriceId: getRequiredEnv(
      env,
      "REACT_APP_STRIPE_PRICE_ID",
      allowTestDefaults ? defaults.stripePriceId : undefined,
    ),
    euStripePriceId: getRequiredEnv(
      env,
      "REACT_APP_EU_STRIPE_PRICE_ID",
      allowTestDefaults ? defaults.euStripePriceId : undefined,
    ),
    redirectLink: getRequiredEnv(
      env,
      "REACT_APP_PAYMENT_REDIRECT_LINK",
      allowTestDefaults ? defaults.redirectLink : undefined,
    ),
    paymentAppBaseUrl: getRequiredEnv(
      env,
      "REACT_APP_PAYMENT_APP_BASE_URL",
      allowTestDefaults ? defaults.paymentAppBaseUrl : undefined,
    ),
    quizImageBaseUrl: getRequiredEnv(
      env,
      "REACT_APP_QUIZ_IMAGE_BASE_URL",
      allowTestDefaults ? defaults.quizImageBaseUrl : undefined,
    ),
    quizPaywallEventUrl: getOptionalEnv(
      env,
      "REACT_APP_QUIZ_PAYWALL_EVENT_URL",
      allowTestDefaults ? defaults.quizPaywallEventUrl : "",
    ),
    upsellOffers: getUpsellOffers(env, allowTestDefaults),
  };

  validatePaymentConfig(config);
  return config;
}

export function validatePaymentConfig(config) {
  const errors = [];

  if (!DEPLOY_ENVS.has(config.deployEnv)) {
    errors.push("REACT_APP_PAYMENT_DEPLOY_ENV must be development, test, staging, or production.");
  }

  for (const [name, value] of [
    ["REACT_APP_PAYMENT_BACKEND_API_URL", config.apiUrl],
    ["REACT_APP_PAYMENT_REDIRECT_LINK", config.redirectLink],
    ["REACT_APP_PAYMENT_APP_BASE_URL", config.paymentAppBaseUrl],
    ["REACT_APP_QUIZ_IMAGE_BASE_URL", config.quizImageBaseUrl],
    ["REACT_APP_QUIZ_PAYWALL_EVENT_URL", config.quizPaywallEventUrl],
  ]) {
    if (value && !isHttpUrl(value)) {
      errors.push(`${name} must be an absolute http(s) URL.`);
    }
  }

  const stripeValues = [
    config.stripePublicKey,
    config.stripePriceId,
    config.euStripePriceId,
    ...Object.values(config.upsellOffers).flatMap((offer) => [
      offer.priceId,
      offer.productId,
    ]),
  ];

  if (config.deployEnv === "staging") {
    if (!config.stripePublicKey.startsWith("pk_test_")) {
      errors.push("staging payment builds require a Stripe test publishable key.");
    }
    for (const value of stripeValues) {
      if (isKnownProductionStripeObject(value)) {
        errors.push("staging payment builds cannot use known production Stripe object IDs.");
        break;
      }
    }
    for (const value of [
      config.apiUrl,
      config.redirectLink === SHARED_RESULT_URL ? "" : config.redirectLink,
      config.paymentAppBaseUrl,
      config.quizImageBaseUrl,
      config.quizPaywallEventUrl,
    ]) {
      if (containsProductionUrl(value)) {
        errors.push("staging payment builds cannot point at production Color Analysis URLs.");
        break;
      }
    }
  }

  if (config.deployEnv === "production" && config.stripePublicKey.startsWith("pk_test_")) {
    errors.push("production payment builds cannot use a Stripe test publishable key.");
  }

  if (errors.length > 0) {
    throw new Error(`Invalid payment config: ${errors.join(" ")}`);
  }
}

function normalizeDeployEnv(rawDeployEnv, nodeEnv) {
  const raw = String(rawDeployEnv || "").trim().toLowerCase();
  if (raw) return raw;
  if (nodeEnv === "test") return "test";
  if (nodeEnv === "production") return "production";
  return "development";
}

function getRequiredEnv(env, name, fallback) {
  const value = getOptionalEnv(env, name, fallback);
  if (value) return value;
  throw new Error(`Missing required payment config env var: ${name}`);
}

function getOptionalEnv(env, name, fallback = "") {
  const value = env[name];
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  return fallback;
}

function getUpsellOffers(env, allowTestDefaults) {
  const rawJson = getOptionalEnv(env, "REACT_APP_UPSELL_OFFERS_JSON");
  if (rawJson) {
    return parseUpsellOffersJson(rawJson);
  }

  const offers = {};
  for (const offerId of ["combo", "hair", "outfits"]) {
    const upperOfferId = offerId.toUpperCase();
    const priceId = getOptionalEnv(env, `REACT_APP_UPSELL_${upperOfferId}_PRICE_ID`);
    const productId = getOptionalEnv(env, `REACT_APP_UPSELL_${upperOfferId}_PRODUCT_ID`);

    if (priceId || productId) {
      if (!priceId || !productId) {
        throw new Error(`Missing price/product pair for upsell offer: ${offerId}`);
      }
      offers[offerId] = { offerId, priceId, productId };
    }
  }

  if (Object.keys(offers).length > 0) {
    return offers;
  }

  if (allowTestDefaults) {
    return TEST_DEFAULTS.upsellOffers;
  }

  throw new Error(
    "Missing required payment config env var: REACT_APP_UPSELL_OFFERS_JSON or REACT_APP_UPSELL_<OFFER>_*.",
  );
}

function parseUpsellOffersJson(rawJson) {
  let parsed;
  try {
    parsed = JSON.parse(rawJson);
  } catch (error) {
    throw new Error("REACT_APP_UPSELL_OFFERS_JSON must be valid JSON.");
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("REACT_APP_UPSELL_OFFERS_JSON must be an object.");
  }

  return Object.entries(parsed).reduce((offers, [offerId, rawOffer]) => {
    if (!rawOffer || typeof rawOffer !== "object" || Array.isArray(rawOffer)) {
      throw new Error(`Invalid upsell offer config: ${offerId}`);
    }
    const priceId = String(rawOffer.priceId || "").trim();
    const productId = String(rawOffer.productId || "").trim();
    if (!priceId || !productId) {
      throw new Error(`Upsell offer ${offerId} requires priceId and productId.`);
    }
    offers[offerId] = { offerId, priceId, productId };
    return offers;
  }, {});
}

function containsProductionUrl(value) {
  return PRODUCTION_URL_MARKERS.some((marker) => String(value || "").includes(marker));
}

function isKnownProductionStripeObject(value) {
  return KNOWN_PRODUCTION_STRIPE_OBJECT_HASHES.has(hashString(String(value || "")));
}

function isHttpUrl(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch (_) {
    return false;
  }
}

function hashString(value) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
}

export const paymentConfig = createPaymentConfig();
